import config from "./config";
import ScreenShare  from "./Screenshare";
import bowser from 'bowser';
import io from "socket.io-client";
// import mediasoupClient from "mediasoup-client";
// import * as mediasoupClient from "mediasoup-client";
import * as mediasoupClient from './bundle';

const VIDEO_CONSTRAINS =
{
  'low':
  {
    width: 320
  },
  'medium':
  {
    width: 640
  },
  'high':
  {
    width: 1280
  },
  'veryhigh':
  {
    width: 1920
  },
  'ultra':
  {
    width: 3840
  }
};

const DEFAULT_NETWORK_PRIORITIES =
{
  audio: 'high',
  mainVideo: 'high',
  additionalVideos: 'medium',
  screenShare: 'medium'
};

function getVideoConstrains(resolution, aspectRatio) {
  return {
    width: { ideal: VIDEO_CONSTRAINS[resolution].width },
    height: { ideal: VIDEO_CONSTRAINS[resolution].width / aspectRatio }
  };
}

const PC_PROPRIETARY_CONSTRAINTS =
{
  optional: [{ googDscp: true }]
};

const VIDEO_SIMULCAST_PROFILES =
{
  3840:
    [
      { scaleResolutionDownBy: 12, maxBitRate: 150000 },
      { scaleResolutionDownBy: 6, maxBitRate: 500000 },
      { scaleResolutionDownBy: 1, maxBitRate: 10000000 }
    ],
  1920:
    [
      { scaleResolutionDownBy: 6, maxBitRate: 150000 },
      { scaleResolutionDownBy: 3, maxBitRate: 500000 },
      { scaleResolutionDownBy: 1, maxBitRate: 3500000 }
    ],
  1280:
    [
      { scaleResolutionDownBy: 4, maxBitRate: 150000 },
      { scaleResolutionDownBy: 2, maxBitRate: 500000 },
      { scaleResolutionDownBy: 1, maxBitRate: 1200000 }
    ],
  640:
    [
      { scaleResolutionDownBy: 2, maxBitRate: 150000 },
      { scaleResolutionDownBy: 1, maxBitRate: 500000 }
    ],
  320:
    [
      { scaleResolutionDownBy: 1, maxBitRate: 150000 }
    ]
};

// Used for VP9 webcam video.
const VIDEO_KSVC_ENCODINGS =
  [
    { scalabilityMode: 'S3T3_KEY' }
  ];

// Used for VP9 desktop sharing.
const VIDEO_SVC_ENCODINGS =
  [
    { scalabilityMode: 'S3T3', dtx: true }
  ];

/**
 * Validates the simulcast `encodings` array extracting the resolution scalings
 * array.
 * ref. https://www.w3.org/TR/webrtc/#rtp-media-api
 *
 * @param {*} encodings
 * @returns the resolution scalings array
 */
function getResolutionScalings(encodings) {
  const resolutionScalings = [];

  // SVC encodings
  if (encodings.length === 1) {
    const { spatialLayers } =
      window.mediasoupClient.parseScalabilityMode(encodings[0].scalabilityMode);

    for (let i = 0; i < spatialLayers; i++) {
      resolutionScalings.push(2 ** (spatialLayers - i - 1));
    }

    return resolutionScalings;
  }

  // Simulcast encodings
  let scaleResolutionDownByDefined = false;

  encodings.forEach((encoding) => {
    if (encoding.scaleResolutionDownBy !== undefined) {
      // at least one scaleResolutionDownBy is defined
      scaleResolutionDownByDefined = true;
      // scaleResolutionDownBy must be >= 1.0
      resolutionScalings.push(Math.max(1.0, encoding.scaleResolutionDownBy));
    }
    else {
      // If encodings contains any encoding whose scaleResolutionDownBy
      // attribute is defined, set any undefined scaleResolutionDownBy
      // of the other encodings to 1.0.
      resolutionScalings.push(1.0);
    }
  });

  // If the scaleResolutionDownBy attribues of sendEncodings are
  // still undefined, initialize each encoding's scaleResolutionDownBy
  // to 2^(length of sendEncodings - encoding index - 1).
  if (!scaleResolutionDownByDefined) {
    encodings.forEach((encoding, index) => {
      resolutionScalings[index] = 2 ** (encodings.length - index - 1);
    });
  }

  return resolutionScalings;
}

const insertableStreamsSupported = Boolean(RTCRtpSender.prototype.createEncodedStreams);

class EventDispatcher {
  constructor() {
    this.listeners = {};
    this.addEventListener = this.addEventListener.bind(this);
    this.removeEventListener = this.removeEventListener.bind(this);
    this.dispatchEvent = this.dispatchEvent.bind(this);
  }

  addEventListener(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  removeEventListener(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
  }

  dispatchEvent(event, data) {
    if (this.listeners[event]) {
      // console.log("recvEvent", this.listeners[event][0])
      this.listeners[event].forEach(async callback => {
        // console.log(await callback(data))
        callback(data)
      });
    }
  }
}

export default class Roomclient extends EventDispatcher {
  constructor({ displayName, device, record, headless } = {}) {
    // Call the constructor of the parent class
    super();

    // Initialize properties
    this._peerid = null;
    this._forceTcp=false;
    this._turnServers=null;
    this._peers=new Map();
    this._roles=null;

    this._displayName = displayName;

    this._connectionSocket = null;

    this._mediasoupDevice = new window.mediasoupClient.Device()

    this._requestRetries = 3

    this._requestTimeout = 20000

    this._sendTransport = null;

    this._recvTransport = null;

    this._produce = true;

    this._device = device;

    this._picture = null;

    this._returning = false;

    this._enableOpusDetails = false;

    this._trackDeviceId = null;

    this._audioTrackDeviceId = null;

    this._webcams = {};

    this._webcamProducer = null;

    this._extraVideoProducers = new Map();

    this._frameRate = config.frameRate;

    this._resolution = config.resolution;

    this._audioDevices = {}

    this._audioOutputDevices = {};

    // Local mic mediasoup Producer.
    this._micProducer = null;

    // Local mic hark
    this._hark = null;

    this._micInProgress = false;

    this._screenSharing = false;

    this._screenSharingProducer = null;

    this._screenSharingAudioProducer = null;

    this._consumers = new Map();

    this._record = record || false;

    this._headless = headless || false;

  }

  timeoutCallback(callback) {
    let called = false;

    const interval = setTimeout(
      () => {
        if (called)
          return;
        called = true;
        // callback(new SocketTimeoutError('Request timed out'));
      },
      this._requestTimeout
    );
    return (...args) => {
      if (called)
        return;
      called = true;
      clearTimeout(interval);

      callback(...args);
    };
  }


  _sendRequest(method, data) {
    return new Promise((resolve, reject) => {
      if (!this._connectionSocket) {
        reject('No socket connection');
      }
      else {
        // console.log('socket connection is there ')
        this._connectionSocket.emit(
          'request',
          { method, data },
          this.timeoutCallback((err, response) => {
            if (err)
              reject(err);
            else
              resolve(response);
          })
        );
      }
    });
  }
  
  async sendRequest(method, data) {
    for (let tries = 0; tries < this._requestRetries; tries++) {
      try {
        // console.log('_sendRequest', method)
        return await this._sendRequest(method, data);
      }
      catch (error) {
        if (
          // error instanceof SocketTimeoutError &&
          tries < this._requestRetries
        ) {
          console.log('sendRequest() | timeout, retrying [attempt:"%s"]', tries);
        }
        else
          {
            this.dispatchEvent('onError', {type: '_sendRequest', error: error});
            throw error;}
      }
    }
  }

  _hookToggleMic()
	{
		if (this._micProducer)
		{
			if (!this._micProducer.paused)
			{
				this.muteMic();
			}
			else
			{
				this.unmuteMic();
			}
		}
		else
		{
			this.updateMic({ start: true });
		}
	}

  async createSendTransport() {
    if (this._produce) {
      const transportInfo = await this.sendRequest(
        'createWebRtcTransport',
        {
          forceTcp: this._forceTcp,
          producing: true,
          consuming: false
        });
// console.log(transportInfo);
      const {
        id,
        iceParameters,
        iceCandidates,
        dtlsParameters
      } = transportInfo;

      this._sendTransport = this._mediasoupDevice.createSendTransport(
        {
          id,
          iceParameters,
          iceCandidates,
          dtlsParameters,
          iceServers: this._turnServers,
          // TODO: Fix for issue #72
          iceTransportPolicy: this._device.flag === 'firefox' && this._turnServers ? 'relay' : undefined,
          proprietaryConstraints: PC_PROPRIETARY_CONSTRAINTS
        });

      this._sendTransport.on(
        'connect', ({ dtlsParameters }, callback, errback) => // eslint-disable-line no-shadow
      {
        this.sendRequest(
          'connectWebRtcTransport',
          {
            transportId: this._sendTransport.id,
            dtlsParameters
          })
          .then(callback)
          .catch(errback);
      });

      this._sendTransport.on(
        'connectionstatechange', (connectState) => {
          switch (connectState) {
            case 'disconnected':
            case 'failed':
              this.restartIce(this._sendTransport, this._sendRestartIce, 2000);
              break;

            default:
              clearTimeout(this._sendRestartIce.timer);
              break;
          }
        });

      this._sendTransport.on(
        'produce', async ({ kind, rtpParameters, appData }, callback, errback) => {
          try {
            // eslint-disable-next-line no-shadow

            // console.log('onproduce')
            const { id } = await this.sendRequest(
              'produce',
              {
                transportId: this._sendTransport.id,
                kind,
                rtpParameters,
                appData
              });

            callback({ id });
          }
          catch (error) {
            this.dispatchEvent('onError', {type: 'onproduce', error: error});
            errback(error);
          }
        });
    }
  }

  async createRecvTransport() {
    const transportInfo = await this.sendRequest(
      'createWebRtcTransport',
      {
        forceTcp: this._forceTcp,
        producing: false,
        consuming: true
      });

    const {
      id,
      iceParameters,
      iceCandidates,
      dtlsParameters
    } = transportInfo;

    this._recvTransport = this._mediasoupDevice.createRecvTransport(
      {
        id,
        iceParameters,
        iceCandidates,
        dtlsParameters,
        iceServers: this._turnServers,
        // TODO: Fix for issue #72
        iceTransportPolicy: this._device.flag === 'firefox' && this._turnServers ? 'relay' : undefined,
        additionalSettings: {
          encodedInsertableStreams: insertableStreamsSupported && this._enableOpusDetails
        },
        appData: {
          encodedInsertableStreams: insertableStreamsSupported && this._enableOpusDetails
        }
      });

    this._recvTransport.on(
      'connect', ({ dtlsParameters }, callback, errback) => // eslint-disable-line no-shadow
    {
      this.sendRequest(
        'connectWebRtcTransport',
        {
          transportId: this._recvTransport.id,
          dtlsParameters
        })
        .then(callback)
        .catch(errback);
    });

    this._recvTransport.on(
      'connectionstatechange', (connectState) => {
        switch (connectState) {
          case 'disconnected':
          case 'failed':
            this.restartIce(this._recvTransport, this._recvRestartIce, 2000);
            break;

          default:
            clearTimeout(this._recvRestartIce.timer);
            break;
        }
      });
  }

  async _updateAudioOutputDevices()
	{
		// logger.debug(_updateAudioOutputDevices()');

		// Reset the list.
		this._audioOutputDevices = {};

		try
		{
			// logger.debug(_updateAudioOutputDevices() | calling enumerateDevices()');

			const devices = await navigator.mediaDevices.enumerateDevices();

			for (const device of devices)
			{
				if (device.kind !== 'audiooutput')
					continue;

				this._audioOutputDevices[device.deviceId] = device;
			}

		}
		catch (error)
		{
			console.log('_updateAudioOutputDevices() [error:"%o"]', error);

      this.dispatchEvent('onError', {type: '_updateAudioOutputDevices', error: error, deviceType: '_updateAudioOutputDevices'})

		}
	}

  async joinRoom(joinwith) {
    const routerRtpCapabilities = await this.sendRequest('getRouterRtpCapabilities');

    routerRtpCapabilities.headerExtensions = routerRtpCapabilities.headerExtensions
      .filter((ext) => ext.uri !== 'urn:3gpp:video-orientation');
      // console.log(routerRtpCapabilities);

      if(!this._mediasoupDevice._loaded){
      
        await this._mediasoupDevice.load({ routerRtpCapabilities });
      
      }
  

    await this.createSendTransport();

    await this.createRecvTransport();

    const {
      // authenticated,
      roles,
      peers,
      // tracker,
      // roomPermissions,
      // userRoles,
      // allowWhenRoleMissing,
      // chatHistory,
      // fileHistory,
      // lastNHistory,
      // locked,
      // lobbyPeers,
      // accessCode,
    } = await this.sendRequest(
      'join',
      {
        displayName: this._displayName,
        picture: this._picture,
        rtpCapabilities: this._mediasoupDevice.rtpCapabilities,
        returning: this._returning
      });
      switch(joinwith){
        case "audio":
          this.updateMic({ start: true });
          break;
        case "video":
          this.updateWebcam({ start: true });
          break;
        case "both":
          this.updateWebcam({ start: true });
          this.updateMic({ start: true });
          break;
        default:
          break;  
      }
      // console.log(this._peers);
      for(let i=0;i<peers.length;i++){
        
        this._peers.set(peers[i].id, peers[i]);
      }
      this._roles=roles;
      // console.log(roles);
      // console.log(this._peers);
      this._updateAudioOutputDevices();
      if(Object.keys(this._audioOutputDevices).length !== 0)
			{
				
        this.dispatchEvent('audioOutputDevice', { deviceId: Object.keys(this._audioOutputDevices)[0] });
			}
      this.dispatchEvent('Joined', { peerList: peers });
      this._roles=roles;
  }
  async  moderatorActionChangeRole(peerId, roleId) {
    await this.sendRequest('moderator:giveRole', { peerId, roleId });
  }
async  moderatorActionMuteMic(peerId) {
  // console.log("into mutemic method");
   await this.sendRequest('moderator:mute', { peerId });
 
}
async  moderatorActionDisableCamera(peerId) {
  await this.sendRequest('moderator:stopVideo', { peerId });
  // console.log(peerId);
  // console.log("into disablecam method");
 
}
  _closeConsumer(consumerId)
	{
		const consumer = this._consumers.get(consumerId);

		if (!consumer)
			return;

		consumer.close();

		this._consumers.delete(consumerId);
	}

  async _resumeConsumer(consumer, { initial = false } = {})
	{
		// console.log('_resumeConsumer() [consumer:"%o"]', consumer);

		if ((!initial && !consumer.paused) || consumer.closed)
			return;

		try
		{
			consumer.resume();
			await this.sendRequest('resumeConsumer', { consumerId: consumer.id });
		}
		catch (error)
		{
			console.log('_resumeConsumer() [consumerId: %s; error:"%o"]', consumer.id, error);
			if (error.notFoundInMediasoupError)
			{
				this._closeConsumer(consumer.id);
			}
      this.dispatchEvent('onError', {type: '_resumeConsumer', error: error});
		}
	}

  async _startConsumer(consumer){
   
    return this._resumeConsumer(consumer, { initial: true });

  }

  async close() {
    
    this._closed = false;

    if (this._closed)
        return;

  this._closed = true;

  // console.log('close()');

  this._connectionSocket.close();

  // Close mediasoup Transports.
  if (this._sendTransport)
      this._sendTransport.close();

  if (this._recvTransport)
      this._recvTransport.close();

    // window.mediasoupClient = undefined;

  sessionStorage.clear();
   
  }

  async closeMeeting() {

		try
		{

			await this.sendRequest('moderator:closeMeeting');

		}
		catch (error)
		{
      this.dispatchEvent('onError', {type: 'closeMeeting', error: error});
			console.log('closeMeeting() [error:"%o"]', error);
		}

  }


  async connect(credentials) {
    console.log('this._record', this._record);
    this._peerid = this.generateUniqueId(credentials.name);
   
    let uid = btoa(credentials.userName);
    const url = `https://${process.env.REACT_APP_mcu}/?peerId=${this._peerid}&roomId=${credentials.roomId}&projectId=62b1adae3f4101b0b4baf3d7&tp=false&_uid=${uid}&record=${this._record}&mDeviceId=null&mDeviceType=null&displayName=${credentials.name}&breakoutRooms=false&breakoutRoomId=null&isHorP=undefined&isHost=${credentials.isHost}&headless=${this._headless}`;
    //  console.log("connect triggered");
    this._connectionSocket = await io(url);

    // console.log('connect to socket', this._connectionSocket);

    this._screenSharing = ScreenShare.create(this._device);

    this._connectionSocket.on('connect', async () => {


      // console.log('connect to socket', this._mediasoupDevice);

      this.dispatchEvent('connected', { message: 'connected to room' });

      // const routerRtpCapabilities = await this.sendRequest('getRouterRtpCapabilities');

      // routerRtpCapabilities.headerExtensions = routerRtpCapabilities.headerExtensions
      //   .filter((ext) => ext.uri !== 'urn:3gpp:video-orientation');

      // await this._mediasoupDevice.load({ routerRtpCapabilities });

    })

    this._connectionSocket.on('disconnect', (reason) => {

      this.dispatchEvent('disconnected', { message: 'room disconnected', reason: reason });

      if (reason === 'io server disconnect')
			{
				this.close();
			}

      if (this._screenSharingProducer)
			{
				this._screenSharingProducer.close();

				this._screenSharingProducer = null;
			}

			if (this._webcamProducer)
			{
				this._webcamProducer.close();

				this._webcamProducer = null;
			}

			for (const producer of this._extraVideoProducers.values())
			{
				producer.close();

			}

			this._extraVideoProducers.clear();

			if (this._micProducer)
			{
				this._micProducer.close();

				this._micProducer = null;
			}

			if (this._sendTransport)
			{
				this._sendTransport.close();

				this._sendTransport = null;
			}

			if (this._recvTransport)
			{
				this._recvTransport.close();

				this._recvTransport = null;
			}


    })

    this._connectionSocket.on('reconnect', (attemptNumber) =>
		{
			// logger.debug(signaling Peer "reconnect" event [attempts:"%s"]', attemptNumber);

      this.dispatchEvent('reconnect', { message: 'reconnect', attemptNumber: attemptNumber });


		});


    this._connectionSocket.on('reconnect_failed', () =>
		{
			// logger.warn('signaling Peer "reconnect_failed" event');

      this.dispatchEvent('reconnect_failed', { message: 'reconnect_failed' });

			this.close();
		});


    this._connectionSocket.on('notification', async (notification) => {
      const { method, data } = notification
      // console.log('notification', method, data)
      // console.log('mediasoup.Device', this._mediasoupDevice);
   
      
      switch (method) {
        case "roomReady":
          this._turnServers = data.turnServers;
          await this.joinRoom(credentials.connectoptions);
          break;
        case "newPeer":
          // console.log(data);
          // console.log(this._peers);
          this._peers.set(data.id, data);
          // console.log(this._peers);
          this.dispatchEvent('newPeer', { message: 'new peer added', data: data });
          break;
        case "peerClosed":
          // console.log(data);
          // console.log(this._peers);
          this._peers.delete(data.peerId);
          // console.log(this._peers);
          this.dispatchEvent('peerclosed', { message: 'peerleft', data: data });
          break;
        case 'moderator:closeMeeting':
          this.close();
          this.dispatchEvent('moderator:closeMeeting', "meeting ended");
          break;
        case  'activeSpeaker':
          this.dispatchEvent('activeSpeaker-changed', data.peerId);
          break;
        case  'consumerClosed':
            this.dispatchEvent('consumerClosed', data.consumerId);
            break;  
        case "recvEvent":
          // console.log("recvEvent...............", data)
          this.dispatchEvent('message-received', data);
            break;    
        case "gotRole":
          this.dispatchEvent('gotRole', data);
            break;    
        case 'newConsumer':
          const {
            peerId,
            producerId,
            id,
            kind,
            rtpParameters,
            type,
            appData,
            producerPaused,
            score
          } = notification.data;

          const consumer = await this._recvTransport.consume(
            {
              id,
              producerId,
              kind,
              rtpParameters,
              appData : { ...appData, peerId } // Trick.
            });

          //Need to check after
          // if (this._recvTransport.appData.encodedInsertableStreams)
          // {
          //   const { enableOpusDetails } = store.getState().settings;

          //   if (kind === 'audio' && enableOpusDetails)
          //     opusReceiverTransform(consumer.rtpReceiver, consumer.id);
          //   else
          //     directReceiverTransform(consumer.rtpReceiver);
          // }

          // console.log('consuming audio/video', consumer);

          // Store in the map.
          this._consumers.set(consumer.id, consumer);

          consumer.on('transportclose', () =>
          {
            this._consumers.delete(consumer.id);
          });

          const { spatialLayers, temporalLayers } =
              window.mediasoupClient.parseScalabilityMode(
                consumer.rtpParameters.encodings[0].scalabilityMode);
          
						const consumerStoreObject = {
							id                     : consumer.id,
							peerId                 : peerId,
							kind                   : kind,
							type                   : type,
							locallyPaused          : false,
							remotelyPaused         : producerPaused,
							rtpParameters          : consumer.rtpParameters,
							source                 : consumer.appData.source,
							width                  : consumer.appData.width,
							height                 : consumer.appData.height,
							resolutionScalings     : consumer.appData.resolutionScalings,
							spatialLayers          : spatialLayers,
							temporalLayers         : temporalLayers,
							preferredSpatialLayer  : 0,
							preferredTemporalLayer : 0,
							priority               : 1,
							codec                  : consumer.rtpParameters.codecs[0].mimeType.split('/')[1],
							track                  : consumer.track,
							score                  : score,
							audioGain              : undefined,
							opusConfig             : null
						};

          await this._startConsumer(consumer);
          this.dispatchEvent("consumer-received",consumerStoreObject);
          break;
        default:
          break;
      }

    })

  }

  _chooseEncodings(simulcastProfiles, size) {
    let encodings;

    const sortedMap = new Map([...Object.entries(simulcastProfiles)]
      .sort((a, b) => parseInt(b[0]) - parseInt(a[0])));

    for (const [key, value] of sortedMap) {
      if (key < size) {
        if (encodings === null) {
          encodings = value;
        }

        break;
      }

      encodings = value;
    }

    // hack as there is a bug in mediasoup
    if (encodings.length === 1) {
      encodings.push({ ...encodings[0] });
    }

    return encodings;
  }

  _getEncodings(width, height, screenSharing = false) {
    // If VP9 is the only available video codec then use SVC.
    const firstVideoCodec = this._mediasoupDevice
      .rtpCapabilities
      .codecs
      .find((c) => c.kind === 'video');

    let encodings;

    const size = (width > height ? width : height);

    if (firstVideoCodec.mimeType.toLowerCase() === 'video/vp9')
      encodings = screenSharing ? VIDEO_SVC_ENCODINGS : VIDEO_KSVC_ENCODINGS;
    else if (config.simulcastProfiles)
      encodings = this._chooseEncodings(config.simulcastProfiles, size);
    else
      encodings = this._chooseEncodings(VIDEO_SIMULCAST_PROFILES, size);

    return encodings;
  }


  async _updateWebcams() {
    // Reset the list.
    this._webcams = {};

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      for (const device of devices) {
        if (device.kind !== 'videoinput')
          continue;

        this._webcams[device.deviceId] = device;
      }

    }
    catch (error) {
      console.log('_updateWebcams() [error:"%o"]', error);
      this.dispatchEvent('onError', {type: 'getDeviceError', error: error, deviceType: '_updateWebcams'})
    }
  }

  async _updateAudioDevices() {

    // Reset the list.
    this._audioDevices = {};

    try {

      const devices = await navigator.mediaDevices.enumerateDevices();

      for (const device of devices) {
        if (device.kind !== 'audioinput')
          continue;

        this._audioDevices[device.deviceId] = device;
      }

    }
    catch (error) {
      console.log('_updateAudioDevices() [error:"%o"]', error);
      this.dispatchEvent('onError', {type: 'getDeviceError', error: error, deviceType: '_updateAudioDevices'})
    }
  }

  async _getAudioDeviceId() {
    try {
      // console.log('_getAudioDeviceId() | calling _updateAudioDeviceId()');

      await this._updateAudioDevices();

      const selectedAudioDevice = this._audioTrackDeviceId;

      if (selectedAudioDevice && this._audioDevices[selectedAudioDevice])
        return selectedAudioDevice;
      else {
        const audioDevices = Object.values(this._audioDevices);

        return audioDevices[0] ? audioDevices[0].deviceId : null;
      }
    }
    catch (error) {
      this.dispatchEvent('onError', {type: 'getDeviceError', error: error, deviceType: '_getAudioDeviceId'})
    }
  }



  async _getWebcamDeviceId() {
    try {
      await this._updateWebcams();

      const selectedWebcam = this._trackDeviceId;

      if (selectedWebcam && this._webcams[selectedWebcam])
        return selectedWebcam;
      else {
        const webcams = Object.values(this._webcams);

        return webcams[0] ? webcams[0].deviceId : null;
      }
    }
    catch (error) {
      console.log('_getWebcamDeviceId() [error:"%o"]', error);
      this.dispatchEvent('onError', {type: 'getDeviceError', error: error, deviceType: '_getWebcamDeviceId'})
    }
  }

  async disableWebcam() {

    if (!this._webcamProducer)
      return;

    this._webcamProducer.close();

    try {
      await this.sendRequest(
        'closeProducer', { producerId: this._webcamProducer.id });
        this.dispatchEvent('closeProducer', {type: 'webcam'})
    }
    catch (error) {
      // const object = {
      //   type: 'camera',
      //   error: 'Unable to update camera'
      // };

      this.dispatchEvent('onError', {type: 'webcam', error: error})
    }

    this._webcamProducer = null;

  }

  async updateWebcam({
    init = false,
    start = false,
    restart = false,
    newDeviceId = null,
    newResolution = null,
    newFrameRate = null
  } = {}) {
    // console.log(
    //   'updateWebcam() [start:"%s", restart:"%s", newDeviceId:"%s", newResolution:"%s", newFrameRate:"%s"]',
    //   start,
    //   restart,
    //   newDeviceId,
    //   newResolution,
    //   newFrameRate
    // );

    let track;

    try {
      // console.log('cannot produce', this._mediasoupDevice)
      if (!this._mediasoupDevice.canProduce('video'))
        throw new Error('cannot produce video');

      if (newDeviceId && !restart)
        throw new Error('changing device requires restart');


      const deviceId = await this._getWebcamDeviceId();

      const device = this._webcams[deviceId];

      if (!device)
        throw new Error('no webcam devices');

      const _frameRate = this._frameRate

      //Resolution
      if ((restart && this._webcamProducer) || start) {
        if (this._webcamProducer)
          await this.disableWebcam();

        try {

          const stream = await navigator.mediaDevices.getUserMedia(
            {
              video:
              {
                deviceId: { ideal: deviceId },
                ...getVideoConstrains(config.resolution, config.aspectRatio), _frameRate
              }
            });
          //  document.getElementById("self").srcObject=stream;
          this.dispatchEvent('stream', { stream: stream });

          ([track] = stream.getVideoTracks());

          const { deviceId: trackDeviceId, width, height } = track.getSettings();

          this._trackDeviceId = trackDeviceId

          // console.log('getUserMedia track settings:', track.getSettings());

          const networkPriority =
            config.networkPriorities?.mainVideo ?
              config.networkPriorities?.mainVideo :
              DEFAULT_NETWORK_PRIORITIES.mainVideo;

          if (this._useSimulcast) {
            const encodings = this._getEncodings(width, height);
            const resolutionScalings = getResolutionScalings(encodings);

            /**
           * TODO:
           * I receive DOMException:
           * Failed to execute 'addTransceiver' on 'RTCPeerConnection':
           * Attempted to set an unimplemented parameter of RtpParameters.
          encodings.forEach((encoding) =>
          {
            encoding.networkPriority=networkPriority;
          });
          */
            encodings[0].networkPriority = networkPriority;

            this._webcamProducer = await this._sendTransport.produce(
              {
                track,
                encodings,
                codecOptions:
                {
                  videoGoogleStartBitrate: 1000
                },
                appData:
                {
                  source: 'webcam',
                  width,
                  height,
                  resolutionScalings
                }
              });
          }
          else {
            this._webcamProducer = await this._sendTransport.produce({
              track,
              encodings: [{ networkPriority }],
              appData:
              {
                source: 'webcam',
                width,
                height
              }
            });
          }
          this._webcamProducer.on('transportclose', () => {
            this._webcamProducer = null;
          });

          this._webcamProducer.on('trackended', () => {

            this.disableWebcam();
          });

        }
        catch (error) {
          
          if (error.name === 'NotAllowedError') {
            this.dispatchEvent('onError', {type: 'handling_webcam', error:"permission-not-handled" });
          }
          else{
            this.dispatchEvent('onError', {type: 'handling_webcam', error: error});
          }
        }
      }
      else if (this._webcamProducer) {
        ({ track } = this._webcamProducer);

        await track.applyConstraints(
          {
            ...getVideoConstrains(config.resolution, config.aspectRatio),
            _frameRate
          }
        );

        // Also change resolution of extra video producers
        for (const producer of this._extraVideoProducers.values()) {
          ({ track } = producer);

          await track.applyConstraints(
            {
              ...getVideoConstrains(config.resolution, config.aspectRatio),
              _frameRate
            }
          );
        }
      }

      await this._updateWebcams();
    }
    catch (error) {
      console.log('updateWebcam() [error:"%o"]', error);

      // const object = {
      //   type: 'camera',
      //   error: 'Unable to update camera'
      // };

      this.dispatchEvent('onError', {type: 'webcam', error: error})

      // window.parent.postMessage(JSON.stringify({ cmd: 'eventError', value: object }), '*');

      if (track)
        track.stop();

      if (error.name === 'NotAllowedError') {
        // Do the necessary UI
        this.dispatchEvent('onError', {type: 'webcam', error: 'Permission Denied'})
      }

    }
  }

  async disableMic() {
    if (!this._micProducer)
      return;

    this._micProducer.close();

    try {
      await this.sendRequest(
        'closeProducer', { producerId: this._micProducer.id });
    }
    catch (error) {
      // const object = {
      //   type: 'mic',
      //   error: 'Unable to update mic'
      // };
      this.dispatchEvent('onError', {type: 'mic', error: error});
      // window.parent.postMessage(JSON.stringify({ cmd: 'eventError', value: object }), '*');
    }

    this._micProducer = null;
  }
async sendMessageToRoom(message){
  // console.log("message sent");
  await this.sendRequest(
    'sendEvent',
    message);
    
}


  async unmuteMic() {
    if (!this._micProducer) {
      this.updateMic({ start: true });
    }
    else {
      this._micProducer.resume();
      try {
        await this.sendRequest('resumeProducer', { producerId: this._micProducer.id });

      }
      catch (error) {
        console.log('unmuteMic() [error:"%o"]', error);

        // const object = {
        //   type: 'mic',
        //   error: 'Unable to unmute mic'
        // };
        this.dispatchEvent('onError', {type: 'mic', error: error});
        // window.parent.postMessage(JSON.stringify({ cmd: 'eventError', value: object }), '*');
      }
    }

  }


  async muteMic() {

    this._micProducer.pause();
    try {
      await this.sendRequest(
        'pauseProducer', { producerId: this._micProducer.id });

    }
    catch (error) {
      console.log('muteMic() [error:"%o"]', error);

      // const object = {
      //   type: 'mic',
      //   error: 'Unable to mute mic'
      // };
      this.dispatchEvent('onError', {type: 'mic', error: error});
      //window.parent.postMessage(JSON.stringify({ cmd: 'eventError', value: object }), '*');

    }

  }

  async updateMic({
    start = false,
    restart = true,
    newDeviceId = null
  } = {}) {
    // console.log(
    //   'updateMic() [start:"%s", restart:"%s", newDeviceId:"%s"]',
    //   start,
    //   restart,
    //   newDeviceId
    // );

    let track;

    try {
      if (this._micInProgress) {
        return;
      }

      if (!this._mediasoupDevice.canProduce('audio'))
        throw new Error('cannot produce audio');

      if (newDeviceId && !restart)
        throw new Error('changing device requires restart');


      const deviceId = await this._getAudioDeviceId();
      const device = this._audioDevices[deviceId];

      if (!device)
        throw new Error('no audio devices');

      const {
        autoGainControl,
        echoCancellation,
        noiseSuppression,
        sampleRate,
        channelCount,
        sampleSize,
        opusStereo,
        opusDtx,
        opusFec,
        opusPtime,
        opusMaxPlaybackRate
      } = config

      if (
        (restart && this._micProducer) ||
        start
      ) {
        this._micInProgress = true;

        let muted = false;

        if (this._micProducer) {
          muted = this._micProducer.paused;
          await this.disableMic();
        }

        const ua = navigator.userAgent;
        const browser = bowser.getParser(ua);

        const stream = await navigator.mediaDevices.getUserMedia(
          {
            audio: {
              deviceId: { ideal: deviceId },
              sampleRate: (browser.satisfies({ safari: '>=0' })) ? undefined : sampleRate,
              channelCount,
              autoGainControl,
              echoCancellation,
              noiseSuppression,
              sampleSize: (browser.satisfies({ safari: '>=0' })) ? undefined : sampleSize
            }

            // audio : true
          }
        );

        ([track] = stream.getAudioTracks());

        const { deviceId: trackDeviceId } = track.getSettings();

        this._audioTrackDeviceId = trackDeviceId

        const networkPriority =
          config.networkPriorities?.audio ?
            config.networkPriorities?.audio :
            DEFAULT_NETWORK_PRIORITIES.audio;

        this._micProducer = await this._sendTransport.produce(
          {
            track,
            encodings:
              [
                {
                  networkPriority
                }
              ],
            codecOptions:
            {
              opusStereo: opusStereo,
              opusFec: opusFec,
              opusDtx: opusDtx,
              opusMaxPlaybackRate: opusMaxPlaybackRate,
              opusPtime: opusPtime
            },
            appData:
              { source: 'mic' }
          });

        if (this._micProducer) {
          this._micInProgress = false;
        }

        this._micProducer.on('transportclose', () => {
          this._micProducer = null;
          this._micInProgress = false;
        });

        this._micProducer.on('trackended', () => {
          this.disableMic();
          this._micInProgress = false;
        });

        if (muted) this.muteMic();
        else this.unmuteMic();
      }
      else if (this._micProducer) {
        ({ track } = this._micProducer);
        this._micInProgress = true;

        const ua = navigator.userAgent;
        const browser = bowser.getParser(ua);

        await track.applyConstraints(
          {
            sampleRate: (browser.satisfies({ safari: '>=0' })) ? undefined : sampleRate,
            channelCount,
            autoGainControl,
            echoCancellation,
            noiseSuppression,
            sampleSize: (browser.satisfies({ safari: '>=0' })) ? undefined : sampleSize
          }
        );

        this._micInProgress = false;
      }

      // TODO update recorder inputs
      /*
      if (recorder != null)
      {
        recorder.addTrack(new MediaStream([ this._micProducer.track ]));
      }
      */
      await this._updateAudioDevices();
    }
    catch (error) {

      // const object = {
      //   type: 'mic',
      //   error: 'Unable to unmute mic'
      // };

      // if ('mediaSession' in navigator && isInPIP()) {
      //   navigator.mediaSession.setMicrophoneActive(false);
      // }
      this.dispatchEvent('onError', {type: 'mic', error: error});
      //window.parent.postMessage(JSON.stringify({ cmd: 'eventError', value: object }), '*');

      if (track)
        track.stop();

      this._micInProgress = false;

      if (error.name === 'NotAllowedError') {
        // Do the necessary UI
        // store.dispatch(roomActions.setInAudioAccesble(false));
      }

    }
  }



  async updateScreenSharing({
		start = false,
		newResolution = null,
		newFrameRate = null
	} = {})
	{
		// console.log('updateScreenSharing() [start:"%s"]', start);

		let track;

		try
		{
			const available = this._screenSharing.isScreenShareAvailable();

			const isAudioEnabled = this._screenSharing.isAudioEnabled();

			if (!available)
				throw new Error('screen sharing not available');

			if (!this._mediasoupDevice.canProduce('video'))
				throw new Error('cannot produce video');

			const {
				screenResolution,
				autoGainControl,
				echoCancellation,
				noiseSuppression,
				aspectRatio,
				screenSharingFrameRate,
				sampleRate,
				channelCount,
				sampleSize,
				opusStereo,
				opusDtx,
				opusFec,
				opusPtime,
				opusMaxPlaybackRate
			} = config;

      
			if (start)
			{
				let stream;

				if (isAudioEnabled)
				{
          // console.log('screenSharing start:');

					stream = await this._screenSharing.start({
						...getVideoConstrains(screenResolution, aspectRatio),
						frameRate : screenSharingFrameRate,
						sampleRate,
						channelCount,
						autoGainControl,
						echoCancellation,
						noiseSuppression,
						sampleSize
					});

				}
				else
				{
          // console.log('screenSharing start:');

					stream = await this._screenSharing.start({
						...getVideoConstrains(screenResolution, aspectRatio),
						frameRate : screenSharingFrameRate
					});

				}
        this.dispatchEvent("selfScreen",{stream:stream});
				([ track ] = stream.getVideoTracks());

				const { width, height } = track.getSettings();


				const networkPriority =
					config.networkPriorities?.screenShare ?
						config.networkPriorities?.screenShare :
						DEFAULT_NETWORK_PRIORITIES.screenShare;

				if (this._useSharingSimulcast)
				{
					let encodings = this._getEncodings(width, height, true);

					// If VP9 is the only available video codec then use SVC.
					const firstVideoCodec = this._mediasoupDevice
						.rtpCapabilities
						.codecs
						.find((c) => c.kind === 'video');

					if (firstVideoCodec.mimeType.toLowerCase() !== 'video/vp9')
					{
						encodings = encodings
							.map((encoding) => ({ ...encoding, dtx: true }));
					}

					const resolutionScalings = getResolutionScalings(encodings);

					/**
					 * TODO:
					 * I receive DOMException:
					 * Failed to execute 'addTransceiver' on 'RTCPeerConnection':
					 * Attempted to set an unimplemented parameter of RtpParameters.
					encodings.forEach((encoding) =>
					{
						encoding.networkPriority=networkPriority;
					});
					*/
					encodings[0].networkPriority = networkPriority;

					this._screenSharingProducer = await this._sendTransport.produce(
						{
							track,
							encodings,
							codecOptions :
							{
								videoGoogleStartBitrate : 1000
							},
							appData :
							{
								source : 'screen',
								width,
								height,
								resolutionScalings
							}
						});
				}
				else
				{
					this._screenSharingProducer = await this._sendTransport.produce({
						track,
						encodings : [ { networkPriority } ],
						appData   :
						{
							source : 'screen',
							width,
							height
						}
					});
				}

				this._screenSharingProducer.on('transportclose', () =>
				{
					this._screenSharingProducer = null;
				});

				this._screenSharingProducer.on('trackended', () =>
				{
					this.disableScreenSharing();
				});

				([ track ] = stream.getAudioTracks());

				if (isAudioEnabled && track)
				{
					this._screenSharingAudioProducer = await this._sendTransport.produce(
						{
							track,
							encodings    : [ { networkPriority } ],
							codecOptions :
							{
								opusStereo          : opusStereo,
								opusFec             : opusFec,
								opusDtx             : opusDtx,
								opusMaxPlaybackRate : opusMaxPlaybackRate,
								opusPtime           : opusPtime
							},
							appData :
							{ source: 'mic' }
						});

					this._screenSharingAudioProducer.on('transportclose', () =>
					{
						this._screenSharingAudioProducer = null;
					});

					this._screenSharingAudioProducer.on('trackended', () =>
					{
						
					});

					this._screenSharingAudioProducer.volume = 0;
				}

				// await this.updateMic({ start: true });

			}
			else
			{
				if (this._screenSharingProducer)
				{
					({ track } = this._screenSharingProducer);

					await track.applyConstraints(
						{
							...getVideoConstrains(screenResolution, aspectRatio),
							frameRate : screenSharingFrameRate
						}
					);
				}

				// if (this._micProducer)
				// {
				// 	await this.unmuteMic();
				// }
				if (this._screenSharingAudioProducer)
				{
					({ track } = this._screenSharingAudioProducer);

					await track.applyConstraints(
						{
							sampleRate,
							channelCount,
							autoGainControl,
							echoCancellation,
							noiseSuppression,
							sampleSize
						}
					);
				}
			}

			if (this._screenSharingProducer)
			{
				window.parent.postMessage(JSON.stringify({ cmd: 'SCREEN_SHARE', value: { screenShare: true, pId: this._peerId } }), '*');
			}

		}
		catch (error)
		{
			console.log('updateScreenSharing() [error:"%o"]', error);

			const object = {
				type  : 'screenShareStart',
				error : error.name === 'NotAllowedError' ? 'Permission denied' : 'An error occurred while accessing your screen'
			};
      this.dispatchEvent('onError', object);
      throw new Error('screen sharing canceled');
			// window.parent.postMessage(JSON.stringify({ cmd: 'eventError', value: object }), '*');


			// if (track)
			// 	track.stop();
		}
	}


  async disableScreenSharing()
	{

		if (!this._screenSharingProducer)
			return;

		// console.log('disableScreenSharing()');

		this._screenSharingProducer.close();

		if (this._screenSharingAudioProducer)
		{
			this._screenSharingAudioProducer.close();

		}

		try
		{
			await this.sendRequest(
				'closeProducer', { producerId: this._screenSharingProducer.id });

			if (this._screenSharingAudioProducer)
			{
				await this.sendRequest(
					'closeProducer', { producerId: this._screenSharingAudioProducer.id });
			}

			window.parent.postMessage(JSON.stringify({ cmd: 'SCREEN_SHARE', value: { screenShare: false, pId: this._peerId } }), '*');

		}
		catch (error)
		{
			console.log('disableScreenSharing() [error:"%o"]', error);
      this.dispatchEvent('onError', {type: 'screenshare', error: error});
		}

		this._screenSharingProducer = null;
		this._screenSharingAudioProducer = null;

		this._screenSharing.stop();
    this.dispatchEvent('screenshare-stopped');
	}

   generateUniqueId(name) {
    const slicedName = name.slice(0, 3)
    const timestampPart = Date.now().toString(36);
    const length = timestampPart.length;
    let timestampPartsliced = timestampPart.slice(length - 4, length - 1);
    const randomPart = Math.floor(Math.random() * 100).toString(36);
    // console.log('generateUniqueId', timestampPartsliced + slicedName + randomPart)
    return timestampPartsliced + slicedName + randomPart;
  }

  //End of RoomClient
}

